/**
 * Botón de solo ícono para los controles de una cabecera o un panel flotante: minimizar, cerrar,
 * quitar, expandir.
 *
 * POR QUÉ EXISTE. Estos controles se venían escribiendo a mano en cada lugar, y cada lugar elegía
 * su propio tamaño. El caso que lo destapó: en la cabecera de un chat acoplado, el guion de
 * minimizar era un `<span>` de 12px y la × era el carácter tipográfico "×" a 15px. Dos cosas
 * distintas —una figura y una letra— puestas una al lado de la otra: la × se veía chica, torcida y
 * fuera de eje. No es un caso aislado; el mismo patrón aparecía en el buscador de clientes y en las
 * líneas de producto del formulario COD.
 *
 * 🪤 LA CAUSA NO ERA EL TAMAÑO, ERA MEZCLAR UN CARÁCTER CON UN ÍCONO. Un glifo "×" se alinea según
 * la métrica de la tipografía —tiene su propio interlineado y su propio centro óptico—, así que
 * nunca queda a eje con un SVG de al lado por más que se le ajuste el `font-size`. Subirle el
 * tamaño lo empeora. La solución es que los dos sean íconos y compartan la misma caja.
 *
 * Por eso el ícono va SIEMPRE en una caja de 16 dentro de un área de 28: el dibujo queda idéntico
 * entre botones y el área de click cumple con lo mínimo táctil sin ocupar más lugar.
 */

import type { IconSource } from '@shopify/polaris';
import { Icon } from '@/lib/polaris';

/**
 * Dos tamaños y no más. `chico` es el de cualquier cabecera o fila; `grande` es para un visor a
 * pantalla completa, donde 16px se pierde sobre la foto. Ofrecer un tamaño libre sería volver al
 * problema: cada pantalla eligiendo el suyo.
 */
const TAMANIOS = {
  chico: { icono: 16, boton: 28 },
  grande: { icono: 20, boton: 40 },
} as const;

export interface AppIconButtonProps {
  icon: IconSource;
  /** Obligatorio: un botón sin texto necesita nombre para quien no ve el dibujo. */
  accessibilityLabel: string;
  onClick?: () => void;
  /**
   * Navega en vez de ejecutar: se dibuja como enlace, con la MISMA caja.
   *
   * 🪤 SU AUSENCIA ERA OTRA PUERTA POR DONDE SE ESQUIVABA EL ESTÁNDAR, igual que pasó con `disabled`.
   * «Ver detalle» y «Llamar» son navegaciones —una a la ficha, otra a `tel:`— y este componente solo
   * sabía ejecutar, así que esas filas usaban el `Button` de Polaris con su propio tamaño. Resultado:
   * en la lista de Ventas los íconos quedaban de un tamaño y en la cabecera del pedido de otro, que es
   * exactamente lo que este archivo existe para evitar.
   *
   * Y no alcanza con un `onClick` que haga `navigate()`: un enlace real se abre en pestaña nueva con
   * el botón del medio y se copia con clic derecho. Cambiarlo por un botón se lo quita a quien
   * trabaja con veinte pedidos abiertos.
   */
  url?: string;
  /** Para cabeceras de color (el verde del chat). Hereda el color del texto del contenedor. */
  tono?: 'inherit' | 'base' | 'subdued' | 'critical';
  tamanio?: keyof typeof TAMANIOS;
  /** Fondo del hover. Claro sobre superficies oscuras, oscuro sobre claras. */
  hover?: 'claro' | 'oscuro';
  /** Impide que el click llegue al contenedor — típico en una cabecera que también es clickeable. */
  detenerPropagacion?: boolean;
  title?: string;
  /**
   * Deshabilitado y «en curso». Están acá, en el estándar, y no resueltos por cada llamador.
   *
   * 🪤 SU AUSENCIA ERA LA RAZÓN POR LA QUE EL ESTÁNDAR SE ESQUIVABA. El botón de llamar necesita
   * deshabilitarse sin teléfono válido y marcar que la llamada está saliendo, así que usaba el
   * `Button` de Polaris con su propio tamaño — y en la cabecera del pedido quedaba grande al lado del
   * ícono de chat, que sí usaba este componente. El founder lo vio: «el icono de chats está chiquito
   * y el de teléfono está grande». Un estándar al que le falta un estado corriente no se respeta: se
   * esquiva, y la inconsistencia vuelve por la puerta de al lado.
   */
  disabled?: boolean;
  cargando?: boolean;
}

export function AppIconButton({
  icon, accessibilityLabel, onClick, url, tono = 'inherit',
  tamanio = 'chico', hover = 'claro', detenerPropagacion, title,
  disabled, cargando,
}: AppIconButtonProps) {
  const inactivo = disabled || cargando;
  const { icono, boton } = TAMANIOS[tamanio];
  const fondoHover = hover === 'claro' ? 'rgba(255,255,255,.18)' : 'rgba(0,0,0,.07)';
  // Un enlace y un botón comparten TODO menos la etiqueta HTML: si el estilo se escribiera dos veces,
  // se separarían al primer ajuste y volveríamos al problema que este archivo vino a cerrar.
  const Etiqueta = url && !inactivo ? 'a' : 'button';
  const propsDeEtiqueta = url && !inactivo
    ? { href: url }
    : { type: 'button' as const, disabled: inactivo };
  return (
    <Etiqueta
      {...propsDeEtiqueta}
      aria-label={accessibilityLabel}
      title={title}
      aria-busy={cargando || undefined}
      aria-disabled={inactivo || undefined}
      onClick={(e: { stopPropagation: () => void }) => {
        if (detenerPropagacion) e.stopPropagation();
        if (inactivo) return;
        onClick?.();
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        textDecoration: 'none',
        width: boton, height: boton, flexShrink: 0,
        padding: 0, background: 'transparent', border: 'none', borderRadius: 6,
        color: 'inherit',
        /*
         * 🪤 EL CURSOR Y LA OPACIDAD SON LA ÚNICA SEÑAL DE QUE NO SE PUEDE. Un ícono de 16px no tiene
         * lugar para un spinner legible; atenuarlo y quitarle el cursor de mano dice «no ahora» sin
         * agregar un dibujo que a ese tamaño sería una mancha.
         */
        cursor: inactivo ? 'default' : 'pointer',
        opacity: inactivo ? 0.45 : 1,
        transition: 'opacity .12s',
      }}
      onMouseEnter={(e: { currentTarget: HTMLElement }) => { if (!inactivo) e.currentTarget.style.background = fondoHover; }}
      onMouseLeave={(e: { currentTarget: HTMLElement }) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* La caja fija es lo que iguala los dibujos: sin ella, cada SVG se dimensiona por su cuenta. */}
      <span style={{ width: icono, height: icono, display: 'block' }}>
        <Icon source={icon} tone={tono} />
      </span>
    </Etiqueta>
  );
}
